import { Link } from 'react-router-dom';
import './WhatHappensPage.css';

const STEPS = [
  {
    n: '1',
    title: 'Collection',
    body: 'Professional team collects scrap from your location in our Leeds van fleet. Full photo documentation and licensed scrap carriers ensure complete compliance from the moment we arrive.',
    image: '/images/what-happens/what-happens-collection.png',
    alt: 'Scrap metal being collected from a property into a collection van',
    highlight: false,
  },
  {
    n: '2',
    title: 'Licensed Transfer & Sorting',
    body: 'Scrap is transported to Environment Agency permitted facilities where metals are sorted by type. Complete compliance documentation and permitted transfer stations guarantee legal, responsible handling.',
    image: '/images/what-happens/what-happens-sorting.png',
    alt: 'Scrap metal sorted by type at a licensed recycling facility',
    highlight: true,
  },
  {
    n: '3',
    title: 'Recycling & Recovery',
    body: 'Metals are sent to specialist processors with 95%+ recovered for reuse. Steel, copper, aluminium, and other metals are recycled into new products. Non-metal residues are handled through approved recovery routes.',
    image: '/images/what-happens/what-happens-recycling.png',
    alt: 'Compressed scrap metal ready for recycling and recovery',
    highlight: false,
  },
];

const BREAKDOWN = [
  { label: 'Recycled ~80%', value: 80, tone: 'green' },
  { label: 'Reused ~50%', value: 50, tone: 'blue' },
  { label: 'Energy Recovery 0%', value: 0, tone: 'orange' },
  { label: 'Landfill 0%', value: 0, tone: 'slate' },
];

function ShieldCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.5l7 2.5v5.2c0 4.3-2.9 7.3-7 9.3-4.1-2-7-5-7-9.3V6L12 3.5z"
        fill="currentColor"
      />
      <path
        d="M9.2 12.1l1.9 1.9 3.7-3.9"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 4.5h6l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-8.5A1.5 1.5 0 0 1 6.5 19V6A1.5 1.5 0 0 1 8 4.5z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path d="M14 4.8V9h4" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M9.5 13h5M9.5 16h3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function ShieldOutlineIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.5l7 2.5v5.2c0 4.3-2.9 7.3-7 9.3-4.1-2-7-5-7-9.3V6L12 3.5z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="6" y="11" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M9 11V8.5a3 3 0 0 1 6 0V11"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PhotoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="6" width="16" height="13" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="9" cy="11" r="1.6" fill="currentColor" />
      <path
        d="M7.5 17l3.2-3.5 2.3 2.2L16 12.5 19 17"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const TRUST_CARDS = [
  {
    title: 'EA Licensed',
    body: 'Upper-tier scrap carrier with all required permits and authorizations',
    Icon: DocIcon,
    tone: 'green',
  },
  {
    title: 'No Fly-Tipping',
    body: 'Only permitted disposal sites with full tracking and compliance',
    Icon: ShieldOutlineIcon,
    tone: 'slate',
  },
  {
    title: 'Legal Protection',
    body: 'Full duty of care compliance documentation for your records',
    Icon: LockIcon,
    tone: 'green',
  },
  {
    title: 'Photo Documentation',
    body: 'Before & after evidence with every single collection',
    Icon: PhotoIcon,
    tone: 'slate',
  },
];

export default function WhatHappensPage() {
  return (
    <div className="what-happens">
      <section className="what-happens-section" aria-labelledby="what-happens-heading">
        <div className="what-happens-inner">
          <header className="what-happens-header">
            <h1 id="what-happens-heading">What Happens to Your Scrap?</h1>
            <p>Transparent processing with industry-leading metal recovery rates</p>
          </header>

          <ol className="what-happens-grid">
            {STEPS.map(({ n, title, body, image, alt, highlight }) => (
              <li key={n} className={`what-happens-card ${highlight ? 'is-highlight' : ''}`}>
                <span className="what-happens-num" aria-hidden="true">
                  {n}
                </span>
                <div className="what-happens-art">
                  <img src={image} alt={alt} loading="lazy" />
                </div>
                <h2>{title}</h2>
                <p>{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="waste-goes" aria-labelledby="waste-goes-heading">
        <div className="what-happens-inner waste-goes-inner">
          <header className="what-happens-header">
            <h2 id="waste-goes-heading">Where Your Scrap Typically Goes</h2>
            <p>Breakdown varies by scrap type and metal stream</p>
          </header>

          <ul className="waste-bars">
            {BREAKDOWN.map((item) => (
              <li key={item.label}>
                <div className="waste-bar-label">{item.label}</div>
                <div
                  className="waste-bar-track"
                  role="meter"
                  aria-label={item.label}
                  aria-valuenow={item.value}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span
                    className={`waste-bar-fill tone-${item.tone}`}
                    style={{ width: `${item.value}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>

          <div className="waste-compliant">
            <span className="waste-compliant-icon">
              <ShieldCheckIcon />
            </span>
            <div>
              <h3>100% Licensed &amp; Compliant</h3>
              <p>
                Every scrap collection handled by licensed professionals with complete documentation
                and legal recycling guarantees
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="waste-trust" aria-label="Compliance highlights and booking">
        <div className="what-happens-inner waste-trust-inner">
          <ul className="waste-trust-grid">
            {TRUST_CARDS.map(({ title, body, Icon, tone }) => (
              <li key={title} className="waste-trust-card">
                <span className={`waste-trust-icon tone-${tone}`}>
                  <Icon />
                </span>
                <h3>{title}</h3>
                <p>{body}</p>
              </li>
            ))}
          </ul>

          <div className="waste-cta">
            <h2>Ready to Move Your Scrap?</h2>
            <p>Post your scrap for free or join as a collector — available online 24/7</p>
            <div className="waste-cta-actions">
              <Link className="waste-cta-btn" to="/post-scrap">
                Post Scrap →
              </Link>
              <Link className="waste-cta-btn waste-cta-btn-secondary" to="/become-a-collector">
                Become a Collector →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
